import { UTCDate } from '@date-fns/utc';
import { subMonths } from 'date-fns';

import {
  RELATIVE_DATE_RANGE_PRESET_MONTHS_BACK,
  RELATIVE_DATE_RANGE_PRESET_UI_LABELS,
  RELATIVE_DATE_RANGE_PRESETS_IN_UI_ORDER,
} from '@revelio/core/global-ai-assistant/date-range-presets';

import { GLOBAL_MIN_START_DATE } from '../utils/providerDateOverrides';

// Month offsets shared with the AI filter pipeline (dash-meta resolves the
// same presets server-side against the data index) — one table, no drift.
const MONTHS_BACK = RELATIVE_DATE_RANGE_PRESET_MONTHS_BACK;

/**
 * The relative presets, built from the shared tokens so the dropdown labels
 * and the assistant's narration cannot drift, and a preset added to the shared
 * table appears here.
 */
const RELATIVE_PRESET_OPTIONS: DateRangeOption[] =
  RELATIVE_DATE_RANGE_PRESETS_IN_UI_ORDER.map((token) => ({
    value: token,
    label: RELATIVE_DATE_RANGE_PRESET_UI_LABELS[token],
    getDateRange: (systemMaxDate: UTCDate) => ({
      start: subMonths(systemMaxDate, MONTHS_BACK[token]) as UTCDate,
      end: systemMaxDate,
    }),
  }));

export type DateRangeOption = {
  value: string;
  label: string;
  hidden?: boolean;
  getDateRange?: (
    systemMaxDate: UTCDate,
    systemMinDate: UTCDate
  ) => { start: UTCDate; end: UTCDate };
};

export type DateOption = {
  value: string;
  label: string;
  hidden?: boolean;
  getDate?: (systemMaxDate: UTCDate) => UTCDate;
};

export const getDateRangeFromPreset = (
  presetValue: string | undefined,
  systemMax: UTCDate,
  systemMin: UTCDate
): { start: UTCDate; end: UTCDate } => {
  const preset = DateRangePresets.find((p) => p.value === presetValue);

  if (preset?.getDateRange) {
    return preset.getDateRange(systemMax, systemMin);
  }

  return { start: systemMin, end: systemMax };
};

export const DateRangePresets: DateRangeOption[] = [
  // Hidden presets for template-level date range overrides
  {
    value: 'posting-provider-range',
    label: 'Posting Provider Range',
    hidden: true,
    getDateRange: (systemMaxDate) => ({
      start: new UTCDate(GLOBAL_MIN_START_DATE),
      end: systemMaxDate,
    }),
  },
  // Visible presets
  ...RELATIVE_PRESET_OPTIONS,
  {
    value: 'max',
    label: 'Max',
    getDateRange: (systemMaxDate, systemMinDate) => ({
      start: systemMinDate,
      end: systemMaxDate,
    }),
  },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * The single-date presets: their own tokens and labels ("6 Months Ago" is a
 * point, "Last 6 Months" a span) over the shared offsets, so the two cannot
 * land a month apart.
 */
const SINGLE_DATE_PRESETS = [
  { value: '6-months-ago', label: '6 Months Ago', from: 'last-6-months' },
  { value: '1-year-ago', label: '1 Year Ago', from: 'last-year' },
  { value: '2-years-ago', label: '2 Years Ago', from: 'last-2-years' },
  { value: '5-years-ago', label: '5 Years Ago', from: 'last-5-years' },
  { value: '10-years-ago', label: '10 Years Ago', from: 'last-10-years' },
] as const satisfies readonly {
  value: string;
  label: string;
  from: keyof typeof MONTHS_BACK;
}[];

export const DatePresets: DateOption[] = [
  ...SINGLE_DATE_PRESETS.map(({ value, label, from }) => ({
    value,
    label,
    getDate: (systemMaxDate: UTCDate) =>
      subMonths(systemMaxDate, MONTHS_BACK[from]) as UTCDate,
  })),
  {
    value: 'latest',
    label: 'Latest',
    getDate: (systemMaxDate) => systemMaxDate,
  },
  { value: 'custom', label: 'Custom Date' },
];
